/* eslint-disable react-hooks/rules-of-hooks */
import axios from 'axios';
import { useState } from 'react';
import { useApi, Res } from './api';
import { useConfig } from '../config';
import { useSession } from './session';
import { createContainer } from 'unstated-next';

const axiosConf = { withCredentials: true };

function buildFavorites() {
	const { isLoggedIn } = useSession();
	const { data, error, mutate, isLoading } = useApi(isLoggedIn ? Res.favorites : null, {});
	let [ids, setIds] = useState<number[]>([]);
	const { api } = useConfig();

	return {
		favorites: data,
		error,
		loading: isLoading,
		async addFavorite(favoriteId: number): Promise<void> {
			const idsCopy = [favoriteId, ...ids];
			setIds(idsCopy);

			try {
				await axios.post(`${api}/me/favorites.json`, { productId: favoriteId }, axiosConf);
			} catch (e) {
				// the star was lit before the write; put it out again rather than let it lie
				setIds(ids.filter((id) => id !== favoriteId));
				return;
			} finally {
				mutate();
			}
		},
		async removeFavorite(favoriteId: number): Promise<void> {
			const favoritesCopy = data?.filter((favorite) => favorite.id !== favoriteId);
			mutate(favoritesCopy, false);
			setIds(ids.filter((id) => id !== favoriteId));

			try {
				await axios.delete(`${api}/me/favorites/${favoriteId}`, axiosConf);
			} catch (e) {
				// FavoriteButton does not await this, so the rejection has nowhere else to go
			} finally {
				/*
				 * No rollback into `ids`. isFavorite reads it *or* the api response, so an id put
				 * back there outlives the revalidation and keeps showing a favourite the server has
				 * already dropped - which is what happens when the delete arrives but its answer
				 * does not. The revalidation is the one that knows.
				 */
				mutate();
			}
		},
		isFavorite(favoriteId: number): boolean {
			const inFastCache = ids.includes(favoriteId);
			const inApiResponse = (data ?? []).some((e) => e.id === favoriteId);
			return inFastCache || inApiResponse;
		},
	};
}

export const FavoritesContainer = createContainer(buildFavorites);

export function useFavorites() {
	return FavoritesContainer.useContainer();
}
